import React, { useState, useEffect } from "react";
import "./CookiesCompliance.css";

const COOKIE_KEY = "cookiePreferences";

const DEFAULTS = {
  necessary: true,
  analytics: false,
  advertising: false,
  socialMedia: false,
};

const COOKIE_INFO = [
  {
    key: "necessary",
    label: "Essential",
    desc: "Required for the site to function correctly. Always active.",
    required: true,
  },
  {
    key: "analytics",
    label: "Performance & Analytics",
    desc: "Helps us understand how visitors interact with the platform.",
    required: false,
  },
  {
    key: "advertising",
    label: "Advertising",
    desc: "Used to serve relevant ads and measure campaign effectiveness.",
    required: false,
  },
  {
    key: "socialMedia",
    label: "Social Media",
    desc: "Enables social sharing features and embedded content.",
    required: false,
  },
];

const CookiesCompliance = ({ onHide }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULTS);

  useEffect(() => {
    const saved = localStorage.getItem(COOKIE_KEY);
    if (!saved) {
      setShowBanner(true);
    } else {
      setPrefs(JSON.parse(saved));
    }
  }, []);

  const persist = (newPrefs) => {
    localStorage.setItem(COOKIE_KEY, JSON.stringify(newPrefs));
    setPrefs(newPrefs);
  };

  const dismiss = () => {
    sessionStorage.setItem("nc_ck_dismissed", "1");
    setShowBanner(false);
    setShowModal(false);
    if (onHide) onHide();
  };

  const handleAcceptAll = () => {
    persist({ necessary: true, analytics: true, advertising: true, socialMedia: true });
    setShowBanner(false);
    if (onHide) onHide();
  };

  const handleNecessaryOnly = () => {
    persist(DEFAULTS);
    setShowBanner(false);
    if (onHide) onHide();
  };

  const handleSavePrefs = () => {
    persist(prefs);
    setShowModal(false);
    setShowBanner(false);
    if (onHide) onHide();
  };

  if (!showBanner) return null;

  return (
    <>
      {/* ── Compact floating banner ── */}
      <div className="ck-banner" role="dialog" aria-label="Cookie preferences">
        <div className="ck-banner-header">
          <span className="ck-banner-title">
            <span className="ck-icon">🍪</span>
            Cookie Preferences
          </span>
          <button className="ck-close-btn" onClick={dismiss} aria-label="Dismiss">✕</button>
        </div>

        <p className="ck-banner-text">
          We use cookies to enhance your experience and analyse site traffic.{" "}
          <a href="/legal/privacy" target="_blank" rel="noreferrer">
            Learn more
          </a>
        </p>

        <div className="ck-banner-actions">
          <button className="ck-btn ck-btn-primary" onClick={handleAcceptAll}>
            Accept All
          </button>
          <button className="ck-btn ck-btn-outline" onClick={handleNecessaryOnly}>
            Necessary Only
          </button>
          <button className="ck-btn ck-btn-ghost" onClick={() => setShowModal(true)}>
            Customize
          </button>
        </div>
      </div>

      {/* ── Preferences modal ── */}
      {showModal && (
        <div className="ck-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="ck-modal">
            <div className="ck-modal-header">
              <h2>Cookie Settings</h2>
              <button className="ck-close-btn" onClick={() => setShowModal(false)} aria-label="Close">✕</button>
            </div>

            <p className="ck-modal-sub">
              Manage how we use cookies. Your choices are saved in your browser.
            </p>

            <div className="ck-modal-items">
              {COOKIE_INFO.map(({ key, label, desc, required }) => (
                <div className="ck-modal-item" key={key}>
                  <div className="ck-modal-item-info">
                    <span className="ck-modal-item-label">{label}</span>
                    <span className="ck-modal-item-desc">{desc}</span>
                  </div>
                  <label className={`ck-toggle-label${required ? " ck-toggle-disabled" : ""}`}>
                    <input
                      type="checkbox"
                      className="ck-toggle-input"
                      checked={prefs[key]}
                      disabled={required}
                      onChange={() => {
                        if (required) return;
                        setPrefs((p) => ({ ...p, [key]: !p[key] }));
                      }}
                    />
                    <span className="ck-toggle-slider" />
                  </label>
                </div>
              ))}
            </div>

            <div className="ck-modal-footer">
              <button className="ck-btn ck-btn-outline" onClick={handleNecessaryOnly}>
                Reject All
              </button>
              <button className="ck-btn ck-btn-primary" onClick={handleSavePrefs}>
                Save Preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookiesCompliance;
